import { Bar } from 'react-chartjs-2';
import {Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend, Title} from 'chart.js';
import Text from "../components/Text";
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Row } from "./Flexbox";
import {MenuItem} from "@mui/material";
import Dropdown from "./Dropdown";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend, Title);


const ProgressBar = React.memo((props) => {
    const chartRef = useRef(null);
    const graphRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const [selectedLegend, setSelectedLegend] = useState(null);

    const maxPerMonth = 43200;
    const individualMax = 14400;
    const areas = ['FLEXO', 'OFFSET', 'OTROS'];
    const [tooltip, setTooltip] = useState({ visible: false, position: { x: 0, y: 0 }, content: [] });

    const [combinedBarWidth, setCombinedBarWidth] = useState(0);

    const maxCapacity = useMemo(() => maxPerMonth * props.data.contributingMonths.size, [props.data.contributingMonths]);   //Total max capacity
    const individualCapacity = useMemo(() => individualMax * props.data.contributingMonths.size, [props.data.contributingMonths]);  //Max capacity per individual bar

    const sourceVals = useMemo(() => {
        const values = props.data.valores || [0, 0, 0, 0];
        return values.map(val => (Math.round(val)));    //Make sure there are no decimals
    }, [props.data.valores]);

    //Receive the sums for the graphs
    const sumTurns = useMemo(() => sourceVals[0] + sourceVals[1] + sourceVals[2], [sourceVals]);
    const surplus = useMemo(() => {
        if (selectedLegend === null) {
            return 0;
        }

        //Get the max capacity for the selected turn, ten substract it from sumTurns (if it's negative, there's no surplus)
        const selectedIndex = parseInt(selectedLegend);
        const currentCapacity = (selectedIndex + 1) * individualCapacity;
        return Math.max(0, sumTurns - currentCapacity);
    }, [sumTurns, selectedLegend, individualCapacity]);

    const displayValues = useMemo(() => {   //Ensures individual bars can't exceed the max value 
        const values = [0, 0, 0];
        const selectedIndex = selectedLegend === null ? 2 : parseInt(selectedLegend);
        const currentCapacity = (selectedIndex + 1) * individualCapacity;
        let remainingVal = Math.min(sumTurns, currentCapacity);    //Ensure the base values don't surpass their capacity
        values[0] = Math.min(remainingVal, individualCapacity);
        remainingVal -= values[0];
        values[1] = Math.min(remainingVal, individualCapacity);
        remainingVal -= values[1];
        values[2] = Math.min(remainingVal, individualCapacity);
        values.push(surplus);   //Add the surplus (there might not be surplus, but this is needed in case there is)

        return values;
    }, [sumTurns, surplus, individualCapacity, selectedLegend]);

    const hexToRgba = (hex, alpha = 1) => {
        hex = hex.replace(/^#/, '');
        if(hex.length === 3) {
            hex = hex.split('').map(c => c + c).join('');
        }

        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);

        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    const baseColor = useCallback(() => {
        const colorMap = {
            FLEXO: props.theme.Magenta,
            OFFSET: props.theme.Yellow,
            OTROS: props.theme.Cyan
        };
        return colorMap[props.data.area] || props.theme.Gray;
    }, [props.theme, props.data.area]);

    const handleSelectChange = (value) => {
        if(props.onAreaChange) {
            props.onAreaChange(props.data.nombre, value)
        }
    }

    const handleLegendChange = (value) => {
        setSelectedLegend(value);
    }

    const config = useMemo(() => ({
        data: {
            labels: [''],
            datasets: displayValues.map((val, i) => ({
                label: i === 3 ? 'Excedente' : i == 0 ? `${i + 1} Turno` : `${i + 1} Turnos`,
                data: [val],
                backgroundColor: i >= 3 ? props.theme.Red : hexToRgba(baseColor(), i === 0 ? 0.25 : i === 1 ? 0.5 : 1),
                stack: 'stack1',
                barThickness: 20,
                borderSkipped: false,
            })).filter(dataset => dataset.data[0] > 0)
        },
        options: {
            type: 'bar',
            indexAxis: 'y', //If "y", bars will stack horizontally
            responsive: true,
            maintainAspectRatio: false,
            onClick: () => setVisible(true), //Needed to show legends
            scales: {
                x: {
                    stacked: true,
                    max: Math.max(maxCapacity, sumTurns),   //Use Math.max to ensure that there's enough space for the surplus by chosing the biggest value.
                    display: false,
                },
                y: {
                    stacked: true,
                    display: false, 
                },
            },
            interaction: {
                mode: 'nearest',
                intersect: false,
            },
            plugins: {
                legend: { 
                    display: false, //Don't change to true, it'll show default labels
                },
                tooltip: { 
                    enabled: false, 
                },
                title: {
                    display: true,
                    text: props.data.nombre,
                    align: 'start',
                    font: {size: 15, weight: 'normal'},
                    color: props.theme.ONED,
                    padding: {
                        bottom: -12
                    }
                },
                datalabels: {
                    display: false
                }
            }
        }
    }), [baseColor, props.data, props.theme.ONED, displayValues, maxCapacity, surplus]);

    const totalPercent = useMemo(() => {
        //Get the index of the current turn selected, using the third turn (index 2) as default
        const currentSelected = selectedLegend === null ? 2 : parseInt(selectedLegend);

        const turnsCount = currentSelected + 1;

        //Get the capacity of the selected turn, and divide sumTurns by it (This helps us to know if there's a surplus per turn)
        const capacity = turnsCount * individualCapacity;

        if (capacity === 0) return 0;        
        //All the accumulated time must be divided for the selected turn capacity
        return Math.round((sumTurns / capacity) * 100);
    }, [selectedLegend, sumTurns, individualCapacity]);

    useEffect(() => {
        if(props.onPercentageChange) {
            props.onPercentageChange(props.data.nombre, totalPercent);
        }
    }, [totalPercent, props.data.nombre, props.onPercentageChange])

    //Handles outside clicks
    const handleOutsideClick = (e) => {
        if (graphRef.current && graphRef.current.contains(e.target)) {  //Avoid closing it if clicked inside
            return; 
        }

        if (e.target.closest('[role="listbox"]')) { //Checks if the click is inside this MUI element to avoid closing it
            return; 
        }

        setVisible(false);
    }

    useEffect(() => { 
        document.addEventListener("mousedown", handleOutsideClick);    //Checks for clicks out of the charts
        return () => document.removeEventListener("mousedown", handleOutsideClick); //Deletes the event 
    }, []); 

    //Initializes and updates chart visibility
    useEffect (() => {
        const chart = chartRef.current;
        if(chart) { //All datasets are visible from start
            
            const datasets = config.data.datasets;

            //Set the default value in the third turn
            if(selectedLegend === null ) {
                setSelectedLegend('2');
            }

            //Apply the visibility state
            datasets.forEach((dataset, dataIndex) => {   //Set the hidden status
                const meta = chart.getDatasetMeta(dataIndex);
                meta.hidden = false;
            });

            chart.update();
        }
    }, [selectedLegend, config.data.datasets]);

    useEffect(() => {
    const chart = chartRef.current;
        if (chart) {
            const xScale = chart.scales.x;

            const selectedIndex = selectedLegend === null ? config.data.datasets.length - 1 : parseInt(selectedLegend);

            let currentVal = config.data.datasets.filter((_, index) => index <= selectedIndex).reduce((sum, dataset) => sum + dataset.data[0], 0);

            if(selectedIndex < 2) {
                const maxSelection = individualCapacity * (selectedIndex + 1);
                currentVal = Math.min(currentVal, maxSelection);
            } else if (selectedIndex === 2) {
                currentVal = Math.min(currentVal, maxCapacity);
            }

            const chartMax = maxCapacity
            currentVal = Math.min(currentVal, chartMax);

            const calculatedWidth = xScale.getPixelForValue(currentVal) - xScale.getPixelForValue(0);
            setCombinedBarWidth(calculatedWidth);
        }
    }, [chartRef, maxCapacity, surplus, individualCapacity, selectedLegend, config.data.datasets]);

    useEffect(() => {
        const graph = graphRef.current;

        const handleMouseMove = (e) => {
            const chart = chartRef.current;
            
            if(!chart) return;

            const rect = chart.canvas.getBoundingClientRect();

            const tooltipX = e.clientX - rect.left;
            const tooltipY = e.clientY - rect.top;

            //Get the selected turn to correctly show the dividend
            const currentSelected = selectedLegend === null ? 2 : parseInt(selectedLegend);
            const turnsCount = currentSelected + 1;
            const selectedCapacity = turnsCount * individualCapacity;

            let tooltipContent = (
                <>
                    <div style={{fontWeight: 'bold', color: surplus > 0 ? props.theme.Red : props.theme.ONED, width: 120, padding: 2, fontSize: '15px', paddingBottom: '8px'}}>{`${sumTurns}/${selectedCapacity} min`}</div>
                    <div style={{color: props.theme.SEVENB, width: 145, padding: 2, fontSize: '11px'}}>{`Setup: ${props.data.setup} min`}</div>
                    {surplus > 0 && (
                        <div style={{color: props.theme.Red, width: 145, padding: 2, fontSize: '11px'}}>{`Excedente: ${surplus} min`}</div>
                    )}
                </>
            );

            //Updates the content and position
            setTooltip({
                visible: true,
                position: {x: tooltipX, y: tooltipY},
                content: tooltipContent
            });
            
        };

        if(graph) {
            graph.addEventListener('mousemove', handleMouseMove);
        }

        return () => {
            if(graph) {
                graph.removeEventListener('mousemove', handleMouseMove);
            }
        };
    }, [sumTurns, surplus, maxCapacity, props.theme, props.data, selectedLegend, individualCapacity]);

    useEffect(() => {
        const graph = graphRef.current;

        const handleMouseLeave = () => {
            setTooltip({visible: false, content: null});
        }

        if(graph) {
            graph.addEventListener('mouseleave', handleMouseLeave);
        } 

        return () => {
            if(graph) {
                graph.removeEventListener('mouseleave', handleMouseLeave);
            }
        }
    }, [])

    if (sumTurns === 0 && (!props.data.contributingMonths || props.data.contributingMonths.size === 0)) {
        return null;
    }

    return (
            <Row style={{alignItems: 'bottom', justifyContent: 'space-between', position: 'relative'}}>
                <div ref={graphRef} style={{width: '87%'}}>
                    <Bar ref={chartRef} data={config.data} options={config.options} height={60}/>
                    {combinedBarWidth > 0 && (
                        //To change the distance between the name and the chart, adjust the top and height values (Adjusting the padding at the title plugin might be necessary too)
                        <div style={{position: 'absolute', top: '55%', transform: 'translateY(-50%)', height: '24px', width: `${combinedBarWidth + 1}px`, border: `2px solid ${baseColor()}`, zIndex: 999, pointerEvents: 'none', boxSizing: 'border-box', left: chartRef.current ? chartRef.current.chartArea.left : 0}}/>
                    )}
                    <>
                        {tooltip.visible && (
                            <div style={{position: 'absolute',  top: tooltip.position.y + 10,  left: tooltip.position.x + 10,  backgroundColor: props.theme.White,  padding: '8px',  borderRadius: '4px',  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.2)', zIndex: 1000, fontSize: '12px', border: '1px solid #ccc'}}>
                                {tooltip.content}
                            </div>
                        )}
                    </>
                    
                    {visible && config.data.datasets.length > 0 && (
                        <div className="custom-legend" style={{ position: 'absolute', top: 80, left: 10, border: '1px solid #ccc', padding: '8px 12px', borderRadius: 4, boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)', backgroundColor: props.theme.White, zIndex: 1000 }} >
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                <Text size={15} weight='medium' color={props.theme.ONED}>{props.data.nombre}</Text>
                                <div style={{height: 12}}></div>
                                <div>
                                    <Dropdown theme={props.theme}  style={{height: 36, width: 180}} value={props.selectedArea || props.data.area} onChange={handleSelectChange} >
                                        {areas.map((area) => (
                                            <MenuItem key={area} value={area} label={area}>{area}</MenuItem>
                                        ))}
                                    </Dropdown>
                                </div>
                                <div style={{height: 12}}></div>
                                <Dropdown theme={props.theme}  style={{height: 36, width: 180}} value={selectedLegend} onChange={handleLegendChange} >
                                    {[0, 1, 2].map((index) => {
                                        const label = index === 0 ? `${index + 1} Turno` : `${index +1} Turnos`;
                                        return (
                                            <MenuItem key={index} value={index.toString()}>{label}</MenuItem>
                                        )
                                    })}
                                </Dropdown>
                            </ul>
                        </div>
                    )}                    
                </div>
                <Text style={{width: 40, height: 19, marginLeft:5, marginTop: 30}} size={15} color={totalPercent > 100 ? props.theme.Red : props.theme.ONED} weight="medium">{`${totalPercent}%`}</Text>
            </Row>
    );
});

export default ProgressBar;